import React, { useState, useRef, useCallback, useEffect } from 'react';
import AsyncSelect from 'react-select/async';

/**
 * Componente generico per filtri con react-select asincrono
 * 
 * @param {Object} props
 * @param {Function} props.loadOptions - Lambda per caricare i dati: (inputValue) => Promise<Array<{value, label}>>
 * @param {Function} props.onSelectionChange - Lambda per gestire la selezione: (selectedOption) => void
 * @param {string} props.placeholder - Placeholder del campo di ricerca
 * @param {boolean} props.isMulti - Se abilitare selezione multipla (default: false)
 * @param {boolean} props.isClearable - Se abilitare la cancellazione (default: true)
 * @param {string} props.className - Classi CSS aggiuntive
 * @param {any} props.value - Valore selezionato (controllato)
 * @param {string} props.noOptionsMessage - Messaggio quando non ci sono opzioni (default: "Nessun risultato trovato")
 * @param {string} props.loadingMessage - Messaggio durante il caricamento (default: "Caricamento...")
 * @param {number} props.debounceDelay - Ritardo per il debounce in ms (default: 300)
 * @param {number} props.minInputLength - Lunghezza minima dell'input per iniziare la ricerca (default: 0)
 */
const FilterSelect = ({
    loadOptions,
    onSelectionChange,
    placeholder = "Cerca...",
    isMulti = false,
    isClearable = true,
    className = "",
    value = null,
    noOptionsMessage = "Nessun risultato trovato",
    loadingMessage = "Caricamento...",
    debounceDelay = 300,
    minInputLength = 0,
    ...otherProps
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const debounceRef = useRef(null);

    // Cleanup del timeout quando il componente viene smontato
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    // Implementazione corretta del debounce con useCallback
    const debouncedLoadOptions = useCallback((inputValue) => {
        // Se l'input è troppo corto, non eseguire la ricerca
        if (inputValue.length < minInputLength) {
            return Promise.resolve([]);
        }

        return new Promise((resolve) => {
            // Cancella il timeout precedente
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            // Imposta un nuovo timeout per il debounce
            debounceRef.current = setTimeout(async () => {
                try {
                    setIsLoading(true);
                    const results = await loadOptions(inputValue);
                    
                    // Assicurati che il risultato sia un array di oggetti con value e label
                    if (!Array.isArray(results)) {
                        console.warn('FilterSelect: loadOptions deve restituire un array');
                        resolve([]);
                        return;
                    }

                    const formattedResults = results.map(item => {
                        // Se l'elemento è già nel formato corretto, usalo così com'è
                        if (item && typeof item === 'object' && 'value' in item && 'label' in item) {
                            return item;
                        }
                        
                        // Altrimenti, prova a convertirlo
                        console.warn('FilterSelect: ogni elemento deve avere proprietà value e label', item);
                        return {
                            value: item.id || item.value || item,
                            label: item.name || item.label || item.toString()
                        };
                    });
                    
                    resolve(formattedResults);
                } catch (error) {
                    console.error('Errore durante il caricamento delle opzioni:', error);
                    resolve([]);
                } finally {
                    setIsLoading(false);
                }
            }, debounceDelay);
        });
    }, [loadOptions, debounceDelay, minInputLength]);

    // Wrapper per la gestione della selezione
    const handleSelectionChange = (selectedOption) => {
        try {
            onSelectionChange(selectedOption);
        } catch (error) {
            console.error('Errore durante la gestione della selezione:', error);
        }
    };

    // Stili personalizzati per react-select
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? '#80bdff' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
            color: state.isSelected ? 'white' : '#212529'
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#e9ecef'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#495057'
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            '&:hover': {
                backgroundColor: '#dc3545',
                color: 'white'
            }
        })
    };

    return (
        <div className={`filter-select-wrapper ${className}`}>
            <AsyncSelect
                cacheOptions
                loadOptions={debouncedLoadOptions}
                onChange={handleSelectionChange}
                placeholder={placeholder}
                isMulti={isMulti}
                isClearable={isClearable}
                value={value}
                noOptionsMessage={() => noOptionsMessage}
                loadingMessage={() => loadingMessage}
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isLoading={isLoading}
                {...otherProps}
            />
        </div>
    );
};

export default FilterSelect;